import React, { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { ImageIcon } from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import { FileChooserDropzoneTrigger } from "./FileChooserDropzoneTrigger";
import { FileUploaderPopup, type FileUploaderProps } from "./FileUploaderPopup";
import { allowedFileTypesList } from "./uploadUtils";

type FileChooserProps = FileUploaderProps & {
  trigger?: React.ReactElement;
  content?: string;
  isBasic?: boolean;
  primary?: boolean;
  secondary?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

export const FileChooser = ({
  isOpen,
  onOpen,
  onClose,
  onComplete,
  onCompleteMultiple,
  trigger,
  isBasic,
  primary,
  secondary,
  content,
  disabled,
  dropzoneProps,
  ...uploaderProps
}: FileChooserProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose();
    }
    setOpen(false);
  }, [open, onClose]);

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup();
    }
    if (!open && onOpen) onOpen();
    setOpen(true);
  }, [open, onOpen, closePopup]);

  const onChosen = useCallback(
    (responseFilename: string, filename: string, responseDownload: string) => {
      onComplete?.(responseFilename, filename, responseDownload);
      closePopup();
    },
    [onComplete, closePopup],
  );
  const onChosenMultiple = useCallback(
    (
      files: Parameters<
        NonNullable<FileUploaderProps["onCompleteMultiple"]>
      >[0],
    ) => {
      onCompleteMultiple?.(files);
      closePopup();
    },
    [onCompleteMultiple, closePopup],
  );

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const triggerElement = useMemo(() => {
    if (trigger) {
      return React.cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
        onKeyDown: handleClickForKeyboard(disabled ? () => {} : openPopup),
      } as React.HTMLAttributes<HTMLElement>);
    }

    return (
      <Button
        size="small"
        disabled={disabled}
        onClick={openPopup}
        color={primary ? "primary" : secondary ? "secondary" : undefined}
        variant={isBasic ? "outline" : "regular"}
      >
        <Icon icon={ImageIcon} />
        {content || t("ideas.details.chooseCover")}
      </Button>
    );
  }, [trigger, openPopup, disabled, isBasic, content, t, primary, secondary]);

  return (
    <>
      {triggerElement}
      <Modal open={open}>
        <ModalHeader>Choose a file</ModalHeader>
        <ModalContent>
          <H3>{t("ideas.actions.chooseImage.info")}</H3>
          <FileUploaderPopup
            onComplete={onChosen}
            onCompleteMultiple={onChosenMultiple}
            onError={toast.error}
            accept={allowedFileTypesList.join(",")}
            dropzoneProps={dropzoneProps}
            {...uploaderProps}
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={closePopup}>{t("generic.close")}</Button>
        </ModalActions>
      </Modal>
    </>
  );
};

type FileChooserWithDropzoneProps = Omit<FileChooserProps, "trigger">;

export const FileChooserWithDropzone = (
  props: FileChooserWithDropzoneProps,
) => {
  return (
    <FileChooser
      trigger={<FileChooserDropzoneTrigger {...props} />}
      {...props}
    />
  );
};
